import React from 'react';
import { arrayOf, bool, func, shape, string, oneOfType, object } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';
import { propTypes } from '../../util/types';
import { maxLength, required, composeValidators } from '../../util/validators';
import { InfoIcon, Form, Button, FieldTextInput, UnsavedInfoPrompt } from '../../components';

import css from './EditProjectListingNameForm.css';

const TITLE_MAX_LENGTH = 60;

const EditProjectListingNameFormComponent = props => {
  return (
    <FinalForm
      {...props}
      render={formRenderProps => {
        const {
          className,
          disabled,
          ready,
          handleSubmit,
          intl,
          invalid,
          pristine,
          saveActionMsg,
          updated,
          updateInProgress,
          fetchErrors,
          dirty,
        } = formRenderProps;

        const titleMessage = intl.formatMessage({ id: 'EditProjectListingDescriptionForm.title' });

        const infoText = intl.formatMessage({ id: 'EditProjectListingNameForm.infoText' });
        const infoListHeader = intl.formatMessage({
          id: 'EditProjectListingNameForm.infoListHeader',
        });
        const infoListFirst = intl.formatMessage({
          id: 'EditProjectListingNameForm.infoListFirst',
        });
        const infoListSecond = intl.formatMessage({
          id: 'EditProjectListingNameForm.infoListSecond',
        });

        const titlePlaceholderMessage = intl.formatMessage({
          id: 'EditProjectListingDescriptionForm.titlePlaceholder',
        });
        const titleRequiredMessage = intl.formatMessage({
          id: 'EditProjectListingDescriptionForm.titleRequired',
        });
        const maxLengthMessage = intl.formatMessage(
          { id: 'EditProjectListingDescriptionForm.maxLength' },
          {
            maxLength: TITLE_MAX_LENGTH,
          }
        );
        const maxLength60Message = maxLength(maxLengthMessage, TITLE_MAX_LENGTH);

        const { updateListingError, createListingDraftError, showListingsError } =
          fetchErrors || {};
        const errorMessageUpdateListing = updateListingError ? (
          <p className={css.error}>
            <FormattedMessage id="EditProjectListingDescriptionForm.updateFailed" />
          </p>
        ) : null;

        // This error happens only on first tab (of EditListingWizard)
        const errorMessageCreateListingDraft = createListingDraftError ? (
          <p className={css.error}>
            <FormattedMessage id="EditProjectListingDescriptionForm.createListingDraftError" />
          </p>
        ) : null;

        const errorMessageShowListing = showListingsError ? (
          <p className={css.error}>
            <FormattedMessage id="EditProjectListingDescriptionForm.showListingFailed" />
          </p>
        ) : null;

        const classes = classNames(css.root, className);
        const submitReady = (updated && pristine) || ready;
        const submitInProgress = updateInProgress;
        const submitDisabled = invalid || disabled || submitInProgress;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            {errorMessageCreateListingDraft}
            {errorMessageUpdateListing}
            {errorMessageShowListing}
            {typeof window === 'object' ? <ReactTooltip /> : null}
            <div className={css.titleBlock}>
              <label htmlFor="title">{titleMessage}</label>
              <InfoIcon
                className={css.infoIcon}
                data-tip={intl.formatMessage({
                  id: 'EditProjectListingDescriptionForm.titleTooltip',
                })}
                data-border={true}
                data-text-color={'black'}
                data-background-color={'white'}
                data-border-color={'darkgrey'}
                data-class={css.tooltipText}
                data-place={'bottom'}
              />
            </div>
            <FieldTextInput
              id="title"
              name="title"
              className={css.title}
              type="text"
              placeholder={titlePlaceholderMessage}
              maxLength={TITLE_MAX_LENGTH}
              validate={composeValidators(required(titleRequiredMessage), maxLength60Message)}
              autoFocus
            />
            <div className={css.infoBlock}>
              <div className={css.infoText}>{infoText}</div>
              <div className={css.infoList}>
                <ul className={css.infoListHeader}>
                  {infoListHeader}
                  <li className={css.infoListItem}>{infoListFirst}</li>
                  <li className={css.infoListItem}>{infoListSecond}</li>
                </ul>
              </div>
            </div>

            <Button
              className={css.submitButton}
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
              ready={submitReady}
            >
              {saveActionMsg}
            </Button>
            <UnsavedInfoPrompt when={dirty} />
          </Form>
        );
      }}
    />
  );
};

EditProjectListingNameFormComponent.defaultProps = { className: null, fetchErrors: null };

EditProjectListingNameFormComponent.propTypes = {
  className: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    createListingDraftError: propTypes.error,
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  categories: arrayOf(
    shape({
      key: string.isRequired,
      label: oneOfType([string, object]).isRequired,
    })
  ),
};

export default compose(injectIntl)(EditProjectListingNameFormComponent);
